import {useEffect, useMemo, useState} from 'react'
import {Link} from 'react-router-dom'
import {format, parseISO} from 'date-fns'
import {
  engagementBand,
  engagementDimensionLabel,
  isSafeInternalPath,
  signalKeyLabel,
} from './rushMindLabels'
import {useNudgeActions} from './useNudgeActions'

export interface RushMindNudge {
  id: number
  signal_key: string
  target_label: string
  target_url: string
  created_at: string
}

export interface RushMindPerformanceBlock {
  engagement_score: number | null
  engagement_breakdown: Record<string, number> | null
  open_nudges: RushMindNudge[]
  resolved_nudges: RushMindNudge[]
  dismissed_nudges: RushMindNudge[]
}

interface Props {
  rushmind: RushMindPerformanceBlock
  isSelf: boolean
  // Permite ao dashboard do colaborador dar um enquadramento próprio ao bloco.
  title?: string
  subtitle?: string
}

const BAND_LABEL: Record<string, string> = {
  green: 'Bom',
  yellow: 'Atenção',
  red: 'Precisa de apoio',
}

const BAND_CLASS: Record<string, string> = {
  green: 'rushmind-score-green',
  yellow: 'rushmind-score-yellow',
  red: 'rushmind-score-red',
}

function formatSince(value: string): string {
  try {
    return format(parseISO(value), 'dd/MM')
  } catch {
    return ''
  }
}

function NudgeLabel({nudge}: {nudge: RushMindNudge}) {
  const since = formatSince(nudge.created_at)
  return (
    <div className='rushmind-section-nudge-target'>
      {isSafeInternalPath(nudge.target_url) ? (
        <Link to={nudge.target_url} className='rushmind-section-nudge-link'>
          {nudge.target_label}
        </Link>
      ) : (
        <span>{nudge.target_label}</span>
      )}
      <span className='rushmind-section-nudge-meta'>
        {' '}
        · {signalKeyLabel(nudge.signal_key)}
        {since ? ` · desde ${since}` : ''}
      </span>
    </div>
  )
}

export function RushMindSection({rushmind, isSelf, title, subtitle}: Props) {
  const {state, check, dismiss, reset} = useNudgeActions()

  // Reseta o estado local das cobranças quando o bloco muda (troca de período/pessoa).
  useEffect(() => {
    reset()
  }, [rushmind]) // eslint-disable-line react-hooks/exhaustive-deps

  const [showHistory, setShowHistory] = useState(false)

  const score = rushmind?.engagement_score ?? null
  const band = engagementBand(score)

  const breakdown = useMemo(() => {
    const entries = Object.entries(rushmind?.engagement_breakdown ?? {})
    // Ordem estável e previsível das dimensões.
    const order = [
      'nudge_adherence',
      'tasks_on_track',
      'sprint_hygiene',
      'time_logging',
      'milestones_wiki',
    ]
    return entries.sort((a, b) => order.indexOf(a[0]) - order.indexOf(b[0]))
  }, [rushmind])

  const visibleOpenNudges = (rushmind?.open_nudges ?? []).filter((n) => !state[n.id]?.removed)
  const historyNudges = [
    ...(rushmind?.resolved_nudges ?? []).map((n) => ({nudge: n, kind: 'Resolvida'})),
    ...(rushmind?.dismissed_nudges ?? []).map((n) => ({nudge: n, kind: 'Ignorada'})),
  ]

  return (
    <div className='dash-section'>
      <div className='dash-section-head mb-6'>
        <div>
          <h2 className='dash-section-title'>{title ?? 'RushMind'}</h2>
          <p className='dash-section-subtitle'>
            {subtitle ??
              'O score reflete hábitos de uso do TaskRush, não desempenho profissional.'}
          </p>
        </div>
      </div>

      <div className='row g-4'>
      <div className='col-12 col-lg-5'>
        <div className='tr-panel h-100'>
          {score === null || band === null ? (
            <div className='rushmind-section-empty'>
              Seu score de engajamento ainda está sendo calculado. Use o TaskRush no dia a dia
              (tarefas em dia, apontamento de horas, cobranças resolvidas) e ele aparece aqui em
              breve.
            </div>
          ) : (
            <>
              <div className='d-flex align-items-baseline gap-3 mb-2'>
                <span className={`rushmind-score-value ${BAND_CLASS[band]}`}>{score}</span>
                <span className={`rushmind-score-band ${BAND_CLASS[band]}`}>
                  {BAND_LABEL[band]}
                </span>
              </div>
              <p className='rushmind-section-hint mb-4'>
                Sobe quando você resolve cobranças, mantém tarefas dentro do prazo e aponta horas.
              </p>
              <div className='d-flex flex-column gap-3'>
                {breakdown.map(([key, value]) => (
                  <div key={key} className='rushmind-score-dim'>
                    <div className='d-flex justify-content-between rushmind-score-dim-head'>
                      <span>{engagementDimensionLabel(key)}</span>
                      <span className='fw-semibold'>{value}</span>
                    </div>
                    <div className='rushmind-score-bar'>
                      <div
                        className='rushmind-score-bar-fill'
                        style={{width: `${Math.max(0, Math.min(100, value))}%`}}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <div className='col-12 col-lg-7'>
        <div className='tr-panel h-100'>
          <div className='fw-bold fs-5 mb-4'>Cobranças abertas</div>

          {visibleOpenNudges.length === 0 ? (
            <div className='rushmind-section-empty'>
              Nenhuma cobrança aberta. Tudo em dia por aqui.
            </div>
          ) : (
            <ul className='rushmind-section-nudge-list'>
              {visibleOpenNudges.map((nudge) => {
                const local = state[nudge.id] || {}
                return (
                  <li key={nudge.id} className='rushmind-section-nudge-item'>
                    <NudgeLabel nudge={nudge} />
                    {isSelf && (
                      <div className='rushmind-section-nudge-actions'>
                        <button
                          type='button'
                          className='rushmind-nudge-btn rushmind-nudge-btn-resolve'
                          onClick={() => check(nudge.id)}
                          disabled={!!local.loading}
                        >
                          Já resolvi
                        </button>
                        <button
                          type='button'
                          className='rushmind-nudge-btn rushmind-nudge-btn-dismiss'
                          onClick={() => dismiss(nudge.id)}
                          disabled={!!local.loading}
                        >
                          Ignorar
                        </button>
                      </div>
                    )}
                    {local.message && <div className='rushmind-nudge-message'>{local.message}</div>}
                  </li>
                )
              })}
            </ul>
          )}

          {historyNudges.length > 0 && (
            <div className='rushmind-section-history mt-5'>
              <button
                type='button'
                className='rushmind-section-history-toggle'
                onClick={() => setShowHistory((v) => !v)}
                aria-expanded={showHistory}
              >
                {showHistory ? 'Ocultar histórico' : `Ver histórico (${historyNudges.length})`}
              </button>
              {showHistory && (
                <ul className='rushmind-section-nudge-list mt-3'>
                  {historyNudges.map(({nudge, kind}) => (
                    <li key={`${kind}-${nudge.id}`} className='rushmind-section-nudge-item'>
                      <NudgeLabel nudge={nudge} />
                      <span
                        className={`rushmind-section-history-tag rushmind-section-history-tag--${
                          kind === 'Resolvida' ? 'resolved' : 'dismissed'
                        }`}
                      >
                        {kind}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
      </div>

      {isSelf && (
        <p className='rushmind-section-pref-note mt-6 mb-0'>
          O resumo semanal do RushMind (no sino e por e-mail) e as demais notificações se ajustam
          em{' '}
          <Link to='/notifications' className='dash-link fw-semibold'>
            Notificações
          </Link>
          .
        </p>
      )}
    </div>
  )
}
